import { useCallback } from 'react';
import { logger } from '@garnish/logger';

import { SNAPCHAT_PIXEL_ID } from './useSnapPixel.constants';
import {
  type Init,
  type TrackAddCart,
  type TrackPageView,
  type TrackPurchase,
  type TrackViewContent,
  type UseSnapPixel,
  type WindowWithSnaptr,
} from './useSnapPixel.types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Initialize and track Snapchat Pixel events.
 *
 * @see {@link https://businesshelp.snapchat.com/s/article/pixel-direct-implementation}
 */
export const useSnapPixel: UseSnapPixel = () => {
  const snaptr = useCallback(
    (...params: Parameters<NonNullable<WindowWithSnaptr['snaptr']>>) => {
      const maybeSnaptr = (window as unknown as WindowWithSnaptr)?.snaptr;

      // ─────────────────────────────────────────────────────

      if (!maybeSnaptr) {
        snapPixelLogger.error('Snap Pixel not found');

        return;
      }

      // ─────────────────────────────────────────────────────

      try {
        maybeSnaptr?.(...params);
      } catch (error) {
        snapPixelLogger.error('Error calling Snap Pixel', { error });
      }
    },
    [],
  );

  // ─── Helpers ─────────────────────────────────────────────────────────

  const init = useCallback<Init>(
    (customer) => {
      if (!customer) {
        snaptr('init', SNAPCHAT_PIXEL_ID);
        snapPixelLogger.info('Initialized Snap Pixel without customer data');

        return;
      }

      const { firstName, lastName, email, phoneNumber } = customer;

      snaptr('init', SNAPCHAT_PIXEL_ID, {
        firstname: firstName,
        lastname: lastName,
        user_email: email,
        user_phone_number: phoneNumber,
      });

      snapPixelLogger.info('Initialized Snap Pixel with customer data');
    },
    [snaptr],
  );

  const trackPageView = useCallback<TrackPageView>(
    (params) => {
      snaptr('track', 'PAGE_VIEW', params);
    },
    [snaptr],
  );

  const trackViewContent = useCallback<TrackViewContent>(
    (params) => {
      snaptr('track', 'VIEW_CONTENT', {
        item_ids: [params.productId],
        description: params.productName,
      });
    },
    [snaptr],
  );

  const trackAddCart = useCallback<TrackAddCart>(
    (params) => {
      snaptr?.('track', 'ADD_CART', {
        currency: 'USD',
        description: params.productName,
        item_ids: [params.productId],
        number_items: params.quantity,
        ...(params.priceInDollars ? { price: params.priceInDollars } : {}),
      });
    },
    [snaptr],
  );

  const trackPurchase = useCallback<TrackPurchase>(
    (params) => {
      snaptr('track', 'PURCHASE', {
        currency: 'USD',
        price: params.priceInDollars,
        transaction_id: params.orderId,
        item_ids: params.productIds,
        number_items: params.lineItemsQuantity,
      });
    },
    [snaptr],
  );

  // ─────────────────────────────────────────────────────────────────────

  return {
    init,
    trackPageView,
    trackViewContent,
    trackAddCart,
    trackPurchase,
  };
};

// ─── Logger ──────────────────────────────────────────────────────────────────

const snapPixelLogger = logger.extend('SNAP_PIXEL');

logger.disable('SNAP_PIXEL');
